import React, { useState, useEffect } from 'react';
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Avatar, Grid, Typography, TextField, IconButton, Button, Badge, CircularProgress } from '@material-ui/core';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import FormHelperText from '@mui/material/FormHelperText';
import { orange } from '@material-ui/core/colors';
import { Tune, Delete, LocalActivity } from '@material-ui/icons';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import Moment from 'react-moment';
import styled from 'styled-components';
import CustomDropdown from '../../../components/Common/Dropdown';
import { MARKETPLACE_ITEMS, MARKETPLACES_TABS } from '../../../helpers/constants';
import GroupSettingsModal from './modals/GroupSettingsModal';
import GroupDeleteModal from './modals/GroupDeleteModal';
import { NormalStyledDarkToggleButton, NormalStyledToggleButton } from '../../../components/Common/ToggleButton';
import DynamicSpaceoutSwitch from './DynamicSpaceoutSwitch';
import { getPricingPageEventLinks } from '../../../helpers/event-search';
import { getUserOrg } from '../../../helpers/user';

const useStyles = makeStyles({
  newNotificationFadeOut: {
    color: orange[500],
    marginTop: '-10px',
    marginLeft: '-2px',
    opacity: 0,
    transition: 'opacity 1s',
    fontSize: '16px',
  },
  newNotificationFadeIn: {
    color: orange[500],
    marginTop: '-10px',
    marginLeft: '-2px',
    fontSize: '16px',
    opacity: 1,
    transition: 'opacity 1s',
  },
  removeUnderline: {
    '& .MuiInput-underline.Mui-disabled:before': {
      borderBottomStyle: 'solid',
      borderBottomColor: 'lightgray',
    },
  },
  detailsMoveEnd: {
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  italicLabel: {
    fontStyle: 'italic',
  },
  InputContainers: {
    display: 'flex',
    gap: '20px',
  },
  buttonProgress: {
    // color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  savedLabel: {
    fontSize: '14px',
    color: '#4CA353',
  },
  customBadge: {
    backgroundColor: orange[500],
    height: 12,
    width: 12,
    borderRadius: 10,
  },
  verticalSeparator: {
    borderLeft: '1px solid grey',
    height: '21px',
    position: 'absolute',
  },
  eventLogo: {
    marginRight: '10px',
    height: '22px',
    width: '22px',
    backgroundColor: '#7D1F8D',
  },
  eventMetaContainer: {
    backgroundColor: '#F4E5F7',
    padding: '6px 20px',
    height: '37px',
  },
  eventMetaContainerDark: {
    backgroundColor: '#5A3C65',
    padding: '6px 20px',
    height: '37px',
  },
  freezingPricingPanelContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    zIndex: 1000,
    marginTop: '2px',
    backgroundColor: 'white',
    marginLeft: '10px',
    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)',
    height: '119px',
  },
  deleteButtonSection: {
    width: '3%',
    height: '22px',
    marginTop: '4px',
  },
  deleteButton: {
    height: '17px',
    width: '17px',
    marginTop: '-5px',
  },
});

const PlusToggleButton = withStyles({
  root: {
    backgroundColor: '#F3F3F3',
    color: '#DBD8DC',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#166544',
      backgroundColor: '#DCFCE7',
      '&:hover': {
        backgroundColor: '#DCFCE7',
      },
      '&.Mui-disabled': {
        color: '#16654480',
        backgroundColor: '#DCFCE780',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#DBD8DC',
      backgroundColor: '#EEEEEEAB',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const PlusDarkToggleButton = withStyles({
  root: {
    backgroundColor: '#00000040',
    color: '#FFFFFF',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#166544',
      backgroundColor: '#DCFCE7',
      '&:hover': {
        backgroundColor: '#DCFCE7',
      },
      '&.Mui-disabled': {
        color: '#166544',
        backgroundColor: '#DCFCE799',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#7D7D7D',
      backgroundColor: '#00000040',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const MinusToggleButton = withStyles({
  root: {
    backgroundColor: '#F3F3F3',
    color: '#DBD8DC',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#991B1B',
      backgroundColor: '#FEE2E2',
      '&:hover': {
        backgroundColor: '#FEE2E2',
      },
      '&.Mui-disabled': {
        color: '#991B1B80',
        backgroundColor: '#FEE2E280',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#DBD8DC',
      backgroundColor: '#EEEEEEAB',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const initialState = {
  isPushEventModalOpen: false,
  isPriceAllLoading: false,
  isGroupSaveLoading: false,
};

const MinusDarkToggleButton = withStyles({
  root: {
    backgroundColor: '#00000040',
    color: '#FFFFFF',
    minWidth: '21px',
    width: '21px',
    height: '21px',
    fontSize: 18,
    '&.Mui-selected': {
      color: '#991B1B',
      backgroundColor: '#FEE2E2',
      '&:hover': {
        backgroundColor: '#FEE2E2',
      },
      '&.Mui-disabled': {
        color: '#991B1B',
        backgroundColor: '#FEE2E299',
        fontStyle: 'italic',
      },
    },
    '&.Mui-disabled': {
      color: '#DBD8DC',
      backgroundColor: '#EEEEEEAB',
      fontStyle: 'italic',
    },
  },
})(ToggleButton);

const MetaHeaderSectionFirst = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
`;

const GroupCriteriaHeader = props => {
  const {
    currentComps,
    errorFlags,
    eventMeta,
    focusedPricingGroup,
    groupedListings,
    handleCeilingError,
    handleGroupAttrEdit,
    handleGroupCriteriaChanges,
    handleGroupMarketplaceSelection,
    handleMarketplaceChange,
    hasCompsSelected,
    handleOV,
    isCompAllEvent,
    isCompAllGroup,
    isSaving,
    marketplaces,
    pricingActions,
    saveZoneFilters,
    selectedIndex,
    selectedPricingGroup,
    setSelectedIndex,
    setSelectedPricingGroup,
    typeListing,
    isLoading,
    isPricingLoading,
    currPricingGroups,
    saveAllSubmit,
    isPriceAllLoading,
    isGroupSaveLoading,
    isVisible,
    groupCriteria,
    incrementSymbol,
    incrementSymbolDefault,
    setSymbolHandler,
    setSymbol,
    setMarketplaceTab,
    marketplaceTab,
    groupFocused,
    isPushEnable,
    badGroups,
    groupsBasicInfo,
    isDynamicFxEvent,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [deleteGroupModalOpen, setDeleteGroupModalOpen] = useState(false);
  const [isManualPriceDisable, setIsManualPriceDisable] = useState(true);
  const userOrg = getUserOrg();
  const eventLinks =
    eventMeta && Object.keys(eventMeta).length > 0
      ? getPricingPageEventLinks(
          eventMeta.links,
          eventMeta.event_id,
          eventMeta.skybox_sales_link,
          eventMeta.reps_links,
          userOrg
        )
      : [];

  function valueToNumber(value) {
    // Remove the dolar sign and convert the value on a float number
    if (typeof value === 'string') {
      const number = parseFloat(value.replace('$', ''));
      return number;
    }
    return value;
  }

  useEffect(() => {
    setSelectedPricingGroup(groupFocused);
  }, [groupFocused]);

  useEffect(() => {
    const marketplace = selectedPricingGroup?.marketplace;
    const marketTab = MARKETPLACES_TABS[Array.isArray(marketplace) ? marketplace[0] : marketplace || ''];
    setMarketplaceTab(marketTab);
    let incrementValue = Number(selectedPricingGroup?.increment);
    if (Number.isNaN(incrementValue)) {
      incrementValue = 0;
    }
    if (selectedPricingGroup?.increment === undefined || incrementValue === 0) {
      setSymbol('-');
    } else if (incrementValue >= 0) {
      setSymbol('+');
    } else {
      setSymbol(incrementSymbolDefault);
    }
  }, [selectedPricingGroup, isVisible, selectedPricingGroup?.increment]);

  // eslint-disable-next-line no-unused-vars
  const handleValue = (field, other = null, callback = null) => (e, newValue) => {
    e.persist();

    if (field === 'splits') {
      const value = other;
      handleGroupAttrEdit(field, value, null)(e);
    } else {
      const { value } = e.target;
      handleGroupAttrEdit(field, value, other)(e);
    }
    if (callback) {
      const { value } = e.target;
      callback(value);
    }
    if (field === 'marketplace') {
      const newMarket = newValue.props ? newValue.props.children : '';
      setMarketplaceTab(MARKETPLACES_TABS[newMarket.toLowerCase()]);
    }
  };

  const handleOnBlur = field => e => {
    const { value } = e.target;
    if (value) {
      handleGroupAttrEdit(field, value)(e);
    }
  };

  const isDisabled = () => {
    let errors = [];
    if (hasCompsSelected) {
      errors = errorFlags.filter(e => e !== 'manual_price' && e.length > 0);
    } else {
      errors = errorFlags.filter(e => e.length > 0);
    }

    return (
      !selectedPricingGroup ||
      errors.length > 0 ||
      selectedPricingGroup.increment === '' ||
      selectedPricingGroup.spaceout === '' ||
      selectedPricingGroup.auto_broadcast === ''
    );
  };

  useEffect(() => {
    setIsManualPriceDisable(!selectedPricingGroup || currentComps.length > 0 || isCompAllEvent || isCompAllGroup);
  }, [selectedPricingGroup, currentComps, isCompAllEvent, isCompAllGroup]);

  const handleSettingsModalOpen = event => {
    event.stopPropagation();
    setSettingsModalOpen(true);
  };

  const handleDeleteGroupModalOpen = event => {
    event.stopPropagation();
    setDeleteGroupModalOpen(true);
  };

  const getSpaceoutValue = () => {
    if (!selectedPricingGroup) return '';
    if (selectedPricingGroup.dynamic_spaceout_enabled) return 0;
    return selectedPricingGroup.spaceout || '';
  };

  const [state, setState] = useState(initialState);

  const isPriceAllDisabled =
    isLoading || isPricingLoading || (currPricingGroups && currPricingGroups.length < 1) || isPriceAllLoading;

  const AdvancedSettingsButtons = (
    <IconButton
      title="Advanced Settings"
      disabled={isDisabled() || settingsModalOpen || (groupsBasicInfo && focusedPricingGroup !== null)}
      aria-label="Advanced Settings"
      onClick={handleSettingsModalOpen}
      color="primary"
      style={{ marginBottom: '5px' }}
    >
      <Tune fontSize="large" />
    </IconButton>
  );

  const deleteButton = (
    <IconButton
      title="delete"
      aria-label="Delete"
      onClick={handleDeleteGroupModalOpen}
      color="primary"
      disabled={!selectedPricingGroup}
      className={classes.deleteButton}
    >
      <Delete fontSize="large" />
    </IconButton>
  );

  const groupSettingsModal = (
    <>
      {selectedPricingGroup && settingsModalOpen && (
        <GroupSettingsModal
          settingsModalOpen={settingsModalOpen}
          setSettingsModalOpen={setSettingsModalOpen}
          pricingGroup={selectedPricingGroup}
          index={selectedIndex}
          groupedListings={groupedListings}
          marketplaces={marketplaces}
          handleMarketplaceChange={handleMarketplaceChange}
          handleOV={handleOV}
          saveZoneFilters={saveZoneFilters}
          handleGroupCriteriaChanges={handleGroupCriteriaChanges}
          currentComps={currentComps}
          handleGroupAttrEdit={handleGroupAttrEdit}
        />
      )}
    </>
  );

  const deleteGroupModal = (
    <GroupDeleteModal
      deleteGroupModalOpen={deleteGroupModalOpen}
      setDeleteGroupModalOpen={setDeleteGroupModalOpen}
      pricingActions={pricingActions}
      pricingGroup={selectedPricingGroup}
      typeListing={typeListing}
      setSelectedPricingGroup={setSelectedPricingGroup}
      setSelectedIndex={setSelectedIndex}
    />
  );

  const nameItem = (
    <Grid item xs={12}>
      <TextField
        shrink={selectedPricingGroup && selectedPricingGroup.name}
        disabled={!selectedPricingGroup || isSaving}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Group Name</Typography>}
        value={(selectedPricingGroup && selectedPricingGroup.name) || ''}
        onChange={handleValue('name')}
        type="text"
        autoFocus={selectedPricingGroup ? focusedPricingGroup === selectedPricingGroup.id : null}
        fullWidth
        className={classes.removeUnderline}
      />
    </Grid>
  );

  const marketplaceItem = (
    <Grid item xs={12}>
      <CustomDropdown
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Marketplace</Typography>}
        id="group-marketplace-select"
        name="group-marketplace"
        items={MARKETPLACE_ITEMS}
        disabled={!selectedPricingGroup || isSaving}
        width="100%"
        shrink={selectedPricingGroup && selectedPricingGroup.marketplace.length > 0}
        selectedValue={marketplaceTab}
        defaultValue={marketplaceTab}
        onChange={handleValue('marketplace', null, handleGroupMarketplaceSelection)}
      />
    </Grid>
  );

  const getFloorHelperText = () => {
    if (selectedPricingGroup && errorFlags.includes('floor')) {
      if (selectedPricingGroup.floor) {
        return 'Floor must be below ceiling';
      }
      return "Floor can't be empty";
    }
    return null;
  };

  const floorItem = (
    <Grid item xs={12}>
      <FormControl
        variant="standard"
        disabled={!selectedPricingGroup || isSaving}
        onChange={handleValue('floor')}
        autoFocus={selectedPricingGroup ? focusedPricingGroup === selectedPricingGroup.id : null}
        className={classes.removeUnderline}
        fullWidth
        error={errorFlags.includes('floor') && selectedPricingGroup}
        style={{ marginTop: '2px' }}
      >
        <InputLabel shrink={Boolean(selectedPricingGroup && selectedPricingGroup.floor)} htmlFor="component-helper">
          <Typography
            className={selectedPricingGroup ? '' : classes.italicLabel}
            style={{ color: errorFlags.includes('floor') ? 'red' : theme.palette.type === 'dark' && '#A8A8A8' }}
          >
            Floor
          </Typography>
        </InputLabel>
        <Input
          type="number"
          value={(selectedPricingGroup && valueToNumber(selectedPricingGroup.floor)) || ''}
          aria-describedby="component-helper-text"
          sx={{
            '&:before': {
              borderBottomColor: theme.palette.type === 'dark' && 'currentColor',
            },
            '&:after': {
              borderBottomColor: '#A049AF',
            },
            '&:hover:not(.Mui-disabled):before': {
              borderBottomColor: theme.palette.type === 'dark' && 'currentColor',
            },
          }}
          style={{ fontSize: '13px', color: theme.palette.type === 'dark' && 'currentColor' }}
        />
        <FormHelperText
          id="component-helper-text"
          style={{
            position: 'absolute',
            width: '150px',
            marginTop: '50px',
            fontSize: '10px',
          }}
        >
          {getFloorHelperText()}
        </FormHelperText>
      </FormControl>
    </Grid>
  );

  const ceilingItem = (
    <Grid item xs={12}>
      <TextField
        shrink={selectedPricingGroup && selectedPricingGroup.ceiling}
        disabled={!selectedPricingGroup || isSaving}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Ceiling</Typography>}
        value={(selectedPricingGroup && valueToNumber(selectedPricingGroup.ceiling)) || ''}
        onChange={handleValue('ceiling')}
        onBlur={handleOnBlur('ceiling', null)}
        error={handleCeilingError(errorFlags.includes('ceiling')) && selectedPricingGroup}
        type="number"
        fullWidth
        className={classes.removeUnderline}
      />
    </Grid>
  );

  const getManualPriceHelperText = () => {
    if (selectedPricingGroup && !isManualPriceDisable && errorFlags.includes('manual_price')) {
      if (selectedPricingGroup.floor && selectedPricingGroup.ceiling) {
        return 'Manual price should be within floor and ceiling';
      }
      if (selectedPricingGroup.floor && selectedPricingGroup.ceiling === '') {
        return 'Manual price must exceed floor';
      }
    }
    return null;
  };

  const manualPriceItem = (
    <Grid item xs={12}>
      <FormControl
        variant="standard"
        disabled={isManualPriceDisable || isSaving}
        onChange={handleValue('manual_price')}
        onBlur={handleOnBlur('manual_price', null)}
        autoFocus={selectedPricingGroup ? focusedPricingGroup === selectedPricingGroup.id : null}
        className={classes.removeUnderline}
        fullWidth
        error={errorFlags.includes('manual_price') && selectedPricingGroup && !isManualPriceDisable}
        style={{ marginTop: '2px' }}
      >
        <InputLabel shrink={selectedPricingGroup && selectedPricingGroup.manualPriceItem} htmlFor="component-helper">
          <Typography
            className={selectedPricingGroup ? '' : classes.italicLabel}
            style={{ color: errorFlags.includes('manual_price') ? 'red' : theme.palette.type === 'dark' && '#A8A8A8' }}
          >
            Manual Price
          </Typography>
        </InputLabel>
        <Input
          id="component-helper"
          type="number"
          value={(!isManualPriceDisable && selectedPricingGroup && selectedPricingGroup.manual_price) || ''}
          aria-describedby="component-helper-text"
          style={{ fontSize: '13px', color: theme.palette.type === 'dark' && 'currentColor' }}
          sx={{
            '&:before': {
              borderBottomColor: theme.palette.type === 'dark' && 'currentColor',
            },
            '&:after': {
              borderBottomColor: '#A049AF',
            },
            '&:hover:not(.Mui-disabled):before': {
              borderBottomColor: theme.palette.type === 'dark' && 'currentColor',
            },
          }}
        />
        <FormHelperText
          style={{
            position: 'absolute',
            width: '250px',
            marginTop: '50px',
            fontSize: '10px',
          }}
        >
          {getManualPriceHelperText()}
        </FormHelperText>
      </FormControl>
    </Grid>
  );

  const incrementItem = (
    <Grid container direction="row" justifyContent="center" style={{ marginBottom: '2px' }}>
      <Grid item style={{ width: '51px' }}>
        <ToggleButtonGroup
          value={incrementSymbol}
          style={{ marginTop: '25px' }}
          exclusive
          onChange={(e, value) => {
            e.persist();
            setSymbolHandler(e, value);
            handleGroupAttrEdit('increment_symbol', null)(e, value);
          }}
          size="small"
          disabled={!selectedPricingGroup || isSaving}
        >
          {theme.palette.type === 'dark' ? (
            <PlusDarkToggleButton value="+" disabled={!selectedPricingGroup}>
              +
            </PlusDarkToggleButton>
          ) : (
            <PlusToggleButton value="+" disabled={!selectedPricingGroup}>
              +
            </PlusToggleButton>
          )}
          {theme.palette.type === 'dark' ? (
            <MinusDarkToggleButton value="-" disabled={!selectedPricingGroup}>
              -
            </MinusDarkToggleButton>
          ) : (
            <MinusToggleButton value="-" disabled={!selectedPricingGroup}>
              -
            </MinusToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
      <Grid item xs={5} style={{ width: 'calc(100% - 102px)' }}>
        <TextField
          shrink={selectedPricingGroup && selectedPricingGroup.increment}
          disabled={!selectedPricingGroup || isSaving}
          label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Increment</Typography>}
          value={
            selectedPricingGroup && selectedPricingGroup.increment !== ''
              ? Math.abs(selectedPricingGroup.increment)
              : ''
          }
          onChange={handleValue('increment', incrementSymbol)}
          type="number"
          fullWidth
          InputProps={{
            step: 0.1,
            style: { fontSize: 14, maxHeight: 22, marginTop: 26 },
          }}
          className={classes.removeUnderline}
        />
      </Grid>
      <Grid item style={{ width: '51px' }}>
        <ToggleButtonGroup
          value={(selectedPricingGroup && selectedPricingGroup.increment_type) || '%'}
          style={{ marginTop: '25px' }}
          exclusive
          onChange={handleGroupAttrEdit('increment_type', null)}
          size="small"
          disabled={!selectedPricingGroup || isSaving}
        >
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton value="$" disabled={!selectedPricingGroup}>
              $
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton value="$" disabled={!selectedPricingGroup}>
              $
            </NormalStyledToggleButton>
          )}
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton value="%" disabled={!selectedPricingGroup}>
              %
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton value="%" disabled={!selectedPricingGroup}>
              %
            </NormalStyledToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );

  const spaceOutItem = (
    <Grid container direction="row" xs={13} justifyContent="center" spacing={1}>
      <Grid item style={{ width: 'calc(100% - 60px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DynamicSpaceoutSwitch
            key={selectedPricingGroup?.id || 'no-group'}
            selectedPricingGroup={selectedPricingGroup}
            isDynamicFxEvent={isDynamicFxEvent}
            handleGroupAttrEdit={handleGroupAttrEdit}
          />
          <TextField
            id="group-spaceout-text-field"
            shrink={selectedPricingGroup && selectedPricingGroup.spaceout}
            label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Spaceout</Typography>}
            value={getSpaceoutValue()}
            onChange={handleValue('spaceout')}
            error={errorFlags.includes('spaceout') && selectedPricingGroup}
            helperText={selectedPricingGroup && errorFlags.includes('spaceout') ? 'Spaceout can not be empty' : ''}
            disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            type="number"
            fullWidth
            InputProps={{
              step: 0.1,
              style: { fontSize: 14, maxHeight: 24, marginTop: 24 },
              inputProps: {
                'data-testid': 'spaceout-text-field',
              },
            }}
            className={classes.removeUnderline}
          />
        </div>
      </Grid>
      <Grid item alignContent="flex-start" style={{ width: '51px' }}>
        <ToggleButtonGroup
          value={(selectedPricingGroup && selectedPricingGroup.spaceout_type) || '%'}
          style={{ marginTop: '25px' }}
          exclusive
          onChange={handleGroupAttrEdit('spaceout_type', null)}
          size="small"
          disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
        >
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton
              value="$"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              $
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton
              value="$"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              $
            </NormalStyledToggleButton>
          )}
          {theme.palette.type === 'dark' ? (
            <NormalStyledDarkToggleButton
              value="%"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              %
            </NormalStyledDarkToggleButton>
          ) : (
            <NormalStyledToggleButton
              value="%"
              disabled={!selectedPricingGroup || isSaving || selectedPricingGroup?.dynamic_spaceout_enabled}
            >
              %
            </NormalStyledToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );

  const autoBroadcastItem = (
    <Grid container xs={12} direction="row">
      <TextField
        shrink={selectedPricingGroup && selectedPricingGroup.auto_broadcast}
        label={<Typography className={selectedPricingGroup ? '' : classes.italicLabel}>Auto Broadcast</Typography>}
        disabled={!selectedPricingGroup || isSaving}
        value={(() => {
          if (selectedPricingGroup) {
            if (selectedPricingGroup.auto_broadcast && selectedPricingGroup.auto_broadcast >= 0) {
              return selectedPricingGroup.auto_broadcast;
            }
            return 0;
          }
          return '';
        })()}
        onChange={handleValue('auto_broadcast')}
        onBlur={handleGroupAttrEdit('auto_broadcast', null)}
        type="Number"
        fullWidth
        className={classes.removeUnderline}
      />
    </Grid>
  );

  const isChanged = selectedPricingGroup && groupCriteria[selectedPricingGroup.id];

  const saveButton = (
    <Badge variant="dot" size="large" classes={{ badge: classes.customBadge }} invisible={!isChanged}>
      <Button
        color="primary"
        variant="contained"
        onClick={() => {
          saveAllSubmit(true);
          setState({ ...state, isGroupSaveLoading: true });
        }}
        disabled={isGroupSaveLoading || isPriceAllDisabled || badGroups.length > 0 || isDisabled()}
      >
        <Typography style={{ fontStyle: 'italic' }}>Save</Typography>
        {isGroupSaveLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
      </Button>
    </Badge>
  );

  const pushToButton = (
    <Button
      color="primary"
      variant="contained"
      onClick={() => {
        saveAllSubmit();
        setState({ ...state, isPriceAllLoading: true });
      }}
      disabled={isGroupSaveLoading || isPriceAllDisabled || badGroups.length > 0 || !isPushEnable}
    >
      <Typography style={{ fontStyle: 'italic' }}>Push to SB</Typography>
      {isPriceAllLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
    </Button>
  );

  const space = `${'\u00a0'.repeat(7)} - ${'\u00a0'.repeat(7)}`;

  return (
    <>
      {isVisible && (
        <>
          <Grid container direction="column" className={classes.freezingPricingPanelContainer}>
            {eventMeta && Object.keys(eventMeta).length > 0 && (
              <Grid
                container
                spacing={24}
                justifyContent="space-between"
                className={theme.palette.type === 'dark' ? classes.eventMetaContainerDark : classes.eventMetaContainer}
              >
                <Grid item style={{ width: '87%', height: '22px' }}>
                  <MetaHeaderSectionFirst>
                    <Avatar
                      aria-label="Event Meta"
                      className={classes.eventLogo}
                      style={{ color: theme.palette.type === 'dark' ? 'white' : 'rgba(255,255,255,1)' }}
                    >
                      <LocalActivity style={{ height: '11px', width: '11px' }} />
                    </Avatar>
                    <Typography inline>
                      <strong>{eventMeta.event_name}</strong>
                      {space}
                      {eventMeta.venue.name}
                      {space}
                      <Moment parse="YYYY-MM-DD HH:mm:ss" format="MM/DD/YY hh:mm a ddd">
                        {eventMeta.event_date_local}
                      </Moment>
                      {space}
                      days until event:&nbsp;
                      {eventMeta.days_until_event}
                    </Typography>
                    {space}
                    <Typography inline>
                      <span>{eventLinks}</span>
                    </Typography>
                  </MetaHeaderSectionFirst>
                </Grid>
                <Grid item style={{ display: 'flex', height: '22px' }}>
                  <Typography
                    style={{
                      marginRight: 'auto',
                      textAlign: 'right',
                      paddingRight: '15px',
                    }}
                  >
                    Pricing Criteria
                  </Typography>
                </Grid>
                <Grid item style={{ height: '22px' }}>
                  <div className={classes.verticalSeparator} />
                </Grid>
                <Grid
                  item
                  className={classes.deleteButtonSection}
                  style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}
                >
                  {deleteButton}
                </Grid>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              style={{
                maxHeight: '82px',
                padding: '0px 10px',
                backgroundColor: theme.palette.type === 'dark' && '#2D3436',
              }}
            >
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                className={classes.InputContainers}
                style={{ marginTop: '3px' }}
              >
                <Grid item style={{ width: '9%' }}>
                  {nameItem}
                </Grid>
                <Grid item style={{ width: '8%' }}>
                  {marketplaceItem}
                </Grid>
                <Grid item style={{ width: '4%' }}>
                  {floorItem}
                </Grid>
                <Grid item style={{ width: '4%' }}>
                  {ceilingItem}
                </Grid>
                <Grid item style={{ width: '7%' }}>
                  {manualPriceItem}
                </Grid>
                <Grid item style={{ width: '14%' }}>
                  {incrementItem}
                </Grid>
                <Grid item style={{ width: '12%' }}>
                  {spaceOutItem}
                </Grid>
                <Grid item style={{ width: '8%' }}>
                  {autoBroadcastItem}
                </Grid>
                <Grid item style={{ width: '21%', display: 'flex' }}>
                  <Grid
                    container
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    spacing={1}
                  >
                    <Grid item xs={2} style={{ display: 'flex', justifyContent: 'end' }}>
                      {AdvancedSettingsButtons}
                    </Grid>
                    <Grid item xs={3} lg={2} style={{ display: 'flex', justifyContent: 'center' }}>
                      {saveButton}
                    </Grid>
                    <Grid item xs={6} lg={5} style={{ display: 'flex', justifyContent: 'center' }}>
                      {pushToButton}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {deleteGroupModal}
          {groupSettingsModal}
        </>
      )}
    </>
  );
};

GroupCriteriaHeader.defaultProps = {
  focusedPricingGroup: null,
  marketplaces: null,
  selectedPricingGroup: null,
};

GroupCriteriaHeader.propTypes = {
  currentComps: PropTypes.array.isRequired,
  errorFlags: PropTypes.array.isRequired,
  eventMeta: PropTypes.object.isRequired,
  focusedPricingGroup: PropTypes.number,
  groupedListings: PropTypes.array.isRequired,
  handleCeilingError: PropTypes.func.isRequired,
  handleGroupAttrEdit: PropTypes.func.isRequired,
  handleGroupCriteriaChanges: PropTypes.func.isRequired,
  handleGroupMarketplaceSelection: PropTypes.func.isRequired,
  handleMarketplaceChange: PropTypes.func.isRequired,
  handleOV: PropTypes.func.isRequired,
  hasCompsSelected: PropTypes.bool.isRequired,
  isCompAllEvent: PropTypes.bool.isRequired,
  isCompAllGroup: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  marketplaces: PropTypes.object,
  pricingActions: PropTypes.object.isRequired,
  saveZoneFilters: PropTypes.func.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  selectedPricingGroup: PropTypes.object,
  setSelectedIndex: PropTypes.func.isRequired,
  setSelectedPricingGroup: PropTypes.func.isRequired,
  typeListing: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPricingLoading: PropTypes.bool.isRequired,
  currPricingGroups: PropTypes.array.isRequired,
  saveAllSubmit: PropTypes.func.isRequired,
  isPriceAllLoading: PropTypes.bool.isRequired,
  isGroupSaveLoading: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  groupCriteria: PropTypes.array.isRequired,
  incrementSymbol: PropTypes.string.isRequired,
  incrementSymbolDefault: PropTypes.string.isRequired,
  setSymbolHandler: PropTypes.func.isRequired,
  setSymbol: PropTypes.func.isRequired,
  setMarketplaceTab: PropTypes.func.isRequired,
  marketplaceTab: PropTypes.number.isRequired,
  groupFocused: PropTypes.object.isRequired,
  isPushEnable: PropTypes.bool.isRequired,
  badGroups: PropTypes.array.isRequired,
  groupsBasicInfo: PropTypes.bool.isRequired,
  isDynamicFxEvent: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    eventMeta: state.pricing.eventMeta,
    groupFocused: state.refreshGroupsInventory.groupFocused,
    isPushEnable: state.refreshGroups.isPushEnable,
    isCompAllEvent: state.groupsStubhubSeatingState.isCompAllEvent,
    badGroups: state.groups.badGroups,
    groupsBasicInfo: state.refreshGroups.groupsBasicInfo,
  };
}

export default connect(mapStateToProps)(GroupCriteriaHeader);
